import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getPaymentsByUser,
    getPaymentSummaryByUser
} from '../../services/paymentService';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { formatPaise } from '../../utils/money';

const RED = '#B22222';
const DARK_RED = '#8B0000';
const GOLD = '#FFD700';

const normalizeList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== 'object') return [];
    const keys = ['data', 'content', 'result', 'items'];
    for (const k of keys) {
        if (Array.isArray(payload[k])) return payload[k];
    }
    return [];
};

// "2026-08-04T10:30:00" se "4 Aug, 10:30 am"
const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May',
        'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const h = d.getHours();
    const m = String(d.getMinutes()).padStart(2, '0');
    const period = h >= 12 ? 'pm' : 'am';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${d.getDate()} ${months[d.getMonth()]}, `
        + `${h12}:${m} ${period}`;
};

const MyPayments = () => {
    const [payments, setPayments] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    const { token, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) {
            navigate('/login');
            return;
        }
        const timer = setTimeout(() => {
            fetchPayments();
        }, 300);
        return () => clearTimeout(timer);
    }, []);

    const fetchPayments = async () => {
        try {
            const userId = user?.id;
            if (!userId) {
                toast.error('Session expired. Login again.');
                navigate('/login');
                return;
            }

            const data = await getPaymentsByUser(userId);
            // Naya payment sabse upar
            const list = normalizeList(data)
                .sort((a, b) => (b.id || 0) - (a.id || 0));
            setPayments(list);

            try {
                const s = await getPaymentSummaryByUser(userId);
                setSummary(s);
            } catch (err) {
                console.log('Could not load summary');
            }

        } catch (error) {
            console.log('Error fetching payments');
            setPayments([]);
        } finally {
            setLoading(false);
        }
    };

    const statusColor = (status) => {
        const map = {
            COMPLETED: { bg: '#EAF3DE', text: '#27500A' },
            PENDING: { bg: '#FFF4E0', text: '#8A5300' },
            FAILED: { bg: '#FCEBEB', text: '#791F1F' },
            REFUNDED: { bg: '#E6F1FB', text: '#0C447C' },
            PARTIALLY_REFUNDED: {
                bg: '#E6F1FB', text: '#0C447C' }
        };
        return map[status] || { bg: '#EFEFEF', text: '#444' };
    };

    const typeLabel = (type) => {
        if (type === 'ADVANCE') return 'Token advance';
        if (type === 'BALANCE') return 'Balance payment';
        if (type === 'FULL') return 'Full payment';
        return type || '-';
    };

    const methodLabel = (m) => {
        const map = {
            UPI: 'UPI',
            CASH: 'Cash',
            CREDIT_CARD: 'Credit card',
            DEBIT_CARD: 'Debit card',
            NET_BANKING: 'Net banking'
        };
        return map[m] || m || '-';
    };

    if (loading) return (
        <div className="text-center py-5">
            <div className="spinner-border"
                style={{ color: RED }}></div>
        </div>
    );

    return (
        <div style={{ backgroundColor: '#FFF5F5' }}
            className="pb-screen">

            {/* ===== HEADER ===== */}
            <div style={{
                backgroundColor: DARK_RED,
                padding: '14px 16px'
            }}>
                <div className="d-flex align-items-center gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: GOLD,
                            fontSize: '22px',
                            lineHeight: 1,
                            padding: 0
                        }}>
                        ←
                    </button>
                    <div>
                        <p className="mb-0 fw-bold"
                            style={{
                                color: GOLD,
                                fontSize: '15px'
                            }}>
                            💰 My Payments
                        </p>
                        <p className="mb-0"
                            style={{
                                color: '#FFE4B5',
                                fontSize: '12px'
                            }}>
                            {payments.length} transaction(s)
                        </p>
                    </div>
                </div>
            </div>

            <div className="container"
                style={{ maxWidth: '560px' }}>
                <div className="py-3">

                    {/* ===== SUMMARY ===== */}
                    {summary && (
                        <div className="card mb-3"
                            style={{
                                borderRadius: '12px',
                                border: `1px solid ${GOLD}`
                            }}>
                            <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="mb-0 fw-bold"
                                            style={{
                                                fontSize: '14px',
                                                color: DARK_RED
                                            }}>
                                            Total paid
                                        </p>
                                        <small className="text-muted">
                                            Across {summary.totalTransactions
                                                || 0} payment(s)
                                        </small>
                                    </div>
                                    <h4 className="fw-bold mb-0"
                                        style={{ color: RED }}>
                                        {formatPaise(summary.totalAmount || 0)}
                                    </h4>
                                </div>

                                {summary.totalRefundAmount > 0 && (
                                    <>
                                        <hr className="my-3" />
                                        <div className="d-flex justify-content-between align-items-center">
                                            <p className="mb-0"
                                                style={{
                                                    fontSize: '14px',
                                                    color: '#0C447C'
                                                }}>
                                                Refunded
                                            </p>
                                            <strong style={{
                                                color: '#0C447C'
                                            }}>
                                                {formatPaise(summary.totalRefundAmount)}
                                            </strong>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ===== PAYMENTS LIST ===== */}
                    {payments.length === 0 ? (
                        <div className="text-center py-5">
                            <h6 style={{ color: DARK_RED }}>
                                No payments yet
                            </h6>
                            <p className="text-muted"
                                style={{ fontSize: '14px' }}>
                                Your payment history will
                                appear here
                            </p>
                            <button
                                className="btn fw-bold mt-2"
                                style={{
                                    backgroundColor: RED,
                                    color: GOLD,
                                    border: 'none'
                                }}
                                onClick={() =>
                                    navigate('/home')}>
                                Book a poojari
                            </button>
                        </div>
                    ) : (
                        <div className="row g-2">
                            {payments.map(p => {
                                const sc = statusColor(
                                    p.paymentStatus);
                                return (
                                    <div key={p.id}
                                        className="col-12">
                                        <div className="card"
                                            style={{
                                                borderRadius: '12px',
                                                border: `1px solid ${GOLD}`
                                            }}>
                                            <div className="card-body p-3">

                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div style={{
                                                        minWidth: 0
                                                    }}>
                                                        <p className="mb-0 fw-bold"
                                                            style={{
                                                                color: DARK_RED,
                                                                fontSize: '14px'
                                                            }}>
                                                            {typeLabel(
                                                                p.paymentType)}
                                                        </p>
                                                        <p className="mb-0 text-muted"
                                                            style={{
                                                                fontSize: '12px'
                                                            }}>
                                                            Booking #{p.bookingId}
                                                            {' · '}
                                                            {methodLabel(
                                                                p.paymentMethod)}
                                                        </p>
                                                    </div>
                                                    <div className="text-end flex-shrink-0 ms-2">
                                                        <div className="fw-bold"
                                                            style={{
                                                                color: RED,
                                                                fontSize: '16px'
                                                            }}>
                                                            {formatPaise(p.amount)}
                                                        </div>
                                                        <span className="badge"
                                                            style={{
                                                                backgroundColor:
                                                                    sc.bg,
                                                                color: sc.text,
                                                                fontSize: '10px'
                                                            }}>
                                                            {p.paymentStatus}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="pt-2"
                                                    style={{
                                                        borderTop:
                                                            '1px solid #eee'
                                                    }}>
                                                    <div className="d-flex justify-content-between"
                                                        style={{
                                                            fontSize: '12px'
                                                        }}>
                                                        <span className="text-muted">
                                                            Transaction ID
                                                        </span>
                                                        <span style={{
                                                            color: DARK_RED,
                                                            wordBreak:
                                                                'break-all'
                                                        }}>
                                                            {p.transactionId
                                                                || '-'}
                                                        </span>
                                                    </div>
                                                    <div className="d-flex justify-content-between mt-1"
                                                        style={{
                                                            fontSize: '12px'
                                                        }}>
                                                        <span className="text-muted">
                                                            Paid on
                                                        </span>
                                                        <span style={{
                                                            color: DARK_RED
                                                        }}>
                                                            {formatDate(
                                                                p.paymentDate
                                                                || p.createdAt)}
                                                        </span>
                                                    </div>

                                                    {p.refundAmount > 0 && (
                                                        <div className="d-flex justify-content-between mt-1"
                                                            style={{
                                                                fontSize: '12px'
                                                            }}>
                                                            <span className="text-muted">
                                                                Refunded
                                                            </span>
                                                            <span style={{
                                                                color: '#0C447C'
                                                            }}>
                                                                {formatPaise(p.refundAmount)}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MyPayments;